import { Switch } from '../../../../components';
import { createQuestionFromConfiguration } from '../../../logbook/views/QuestionView';
import {
    Card,
    Exercise,
    GhostButton,
    LabelBody,
    LabelBodyHeadline,
    LabelBodySmall,
    LabelDisplay1,
    LabelDisplay2,
    LabelDisplay4,
    LabelHeader4,
    ProgressBar,
    SecondaryButton,
    Spacer,
    Textarea,
} from '../../components';
import { ExerciseDto, ExerciseStatus } from '../../interfaces';
import { VideoExerciseView } from '../../views';
import { normalizeInput, useSyncedState } from '../../useSyncedState';
import { ExerciseData, ExerciseViewProps } from '../../exercises';
import { Module7Exercise3Illustration1 } from '../../icons';
import { useNavigation } from '../../useNavigation';
import { IllustrationCritic } from '../../icons/IllustrationCritic';

const page2HasSeenVideoKey = 'page2HasSeenVideo';
const page4HasSeenVideoKey = 'page4HasSeenVideo';
const page6HasSeenVideoKey = 'page6HasSeenVideo';
const page3HowWouldYouHelpAFriendKey = 'page3HowWouldYouHelpAFriend';
const page5HowWouldYouHelpYourselfKey = 'page5HowWouldYouHelpYourself';
const page7ReflectOnTheExerciseKey = 'page7ReflectOnTheExercise';

export const exerciseData32: ExerciseData = {
    ExerciseView: Module3Exercise2,
    dataKeys: [
        page3HowWouldYouHelpAFriendKey,
        page5HowWouldYouHelpYourselfKey,
        page7ReflectOnTheExerciseKey,
    ],
    questions: [
        createQuestionFromConfiguration({
            dataKeys: [page3HowWouldYouHelpAFriendKey],
            title: 'Hvordan ville du hjælpe en ven?',
            description:
                'Tænk over, hvordan du ville forsøge at være der for personen. Hvad ville du sige, og hvordan ville du hjælpe?',
        }),
        createQuestionFromConfiguration({
            dataKeys: [page5HowWouldYouHelpYourselfKey],
            title: 'Hvordan ville du hjælpe dig selv?',
            description:
                'Tænk nu over, hvordan du ville forsøge at være der for den udgave af dig, der kan have det svært. Hvad ville du sige, og hvordan ville du hjælpe?',
        }),
        createQuestionFromConfiguration({
            dataKeys: [page7ReflectOnTheExerciseKey],
            title: 'Reflekter over øvelsen',
            description:
                'Overvej nu, om der er forskel på den måde, du vil hjælpe en ven på sammenlignet med dig selv. Hvad kan du tage med dig fra øvelsen, så du bedre husker en større forståelse og omsorg for dig selv?',
        }),
    ],
};

function Module3Exercise2({ exercise, onContinue }: ExerciseViewProps) {
    const [
        page3HowWouldYouHelpAFriendInput,
        setPage3HowWouldYouHelpAFriendInput,
    ] = useSyncedState(exercise.Data[page3HowWouldYouHelpAFriendKey] ?? '');

    const [
        page5HowWouldYouHelpYourselfInput,
        setPage5HowWouldYouHelpYourselfInput,
    ] = useSyncedState(exercise.Data[page5HowWouldYouHelpYourselfKey] ?? '');

    const [page7ReflectOnTheExerciseInput, setPage7ReflectOnTheExerciseInput] =
        useSyncedState(exercise.Data[page7ReflectOnTheExerciseKey] ?? '');

    return (
        <Exercise title="Oplever du selvkritik?">
            <ProgressBar current={exercise.CurrentPage} total={8} />
            <Switch
                value={exercise.CurrentPage}
                default={1}
                cases={{
                    1: <Page1 exercise={exercise} onContinue={onContinue} />,
                    2: (
                        <Page2
                            exercise={exercise}
                            page2HasSeenVideo={
                                exercise.Data[page2HasSeenVideoKey] ?? 'false'
                            }
                            onContinue={() => {
                                onContinue({
                                    status: ExerciseStatus.InProgress,
                                    pageNumber: 3,
                                    data: {
                                        [page2HasSeenVideoKey]: 'true',
                                    },
                                });
                            }}
                        />
                    ),
                    3: (
                        <Page3
                            page3HowWouldYouHelpAFriendInput={
                                page3HowWouldYouHelpAFriendInput
                            }
                            onChangePage3HowWouldYouHelpAFriendInput={
                                setPage3HowWouldYouHelpAFriendInput
                            }
                            onContinue={() =>
                                onContinue({
                                    status: ExerciseStatus.InProgress,
                                    pageNumber: 4,
                                    data: {
                                        [page3HowWouldYouHelpAFriendKey]:
                                            normalizeInput(
                                                page3HowWouldYouHelpAFriendInput,
                                            ),
                                    },
                                })
                            }
                        />
                    ),
                    4: (
                        <Page4
                            exercise={exercise}
                            page4HasSeenVideo={
                                exercise.Data[page4HasSeenVideoKey] ?? 'false'
                            }
                            onContinue={() => {
                                onContinue({
                                    status: ExerciseStatus.InProgress,
                                    pageNumber: 5,
                                    data: {
                                        [page4HasSeenVideoKey]: 'true',
                                    },
                                });
                            }}
                        />
                    ),
                    5: (
                        <Page5
                            page5HowWouldYouHelpYourselfInput={
                                page5HowWouldYouHelpYourselfInput
                            }
                            onChangePage5HowWouldYouHelpYourselfInput={
                                setPage5HowWouldYouHelpYourselfInput
                            }
                            onContinue={() =>
                                onContinue({
                                    status: ExerciseStatus.InProgress,
                                    pageNumber: 6,
                                    data: {
                                        [page5HowWouldYouHelpYourselfKey]:
                                            normalizeInput(
                                                page5HowWouldYouHelpYourselfInput,
                                            ),
                                    },
                                })
                            }
                        />
                    ),
                    6: (
                        <Page6
                            exercise={exercise}
                            page6HasSeenVideo={
                                exercise.Data[page6HasSeenVideoKey] ?? 'false'
                            }
                            onContinue={() => {
                                onContinue({
                                    status: ExerciseStatus.InProgress,
                                    pageNumber: 7,
                                    data: {
                                        [page6HasSeenVideoKey]: 'true',
                                    },
                                });
                            }}
                        />
                    ),
                    7: (
                        <Page7
                            page7ReflectOnTheExerciseInput={
                                page7ReflectOnTheExerciseInput
                            }
                            onChangePage7ReflectOnTheExerciseInput={
                                setPage7ReflectOnTheExerciseInput
                            }
                            onContinue={() =>
                                onContinue({
                                    status: ExerciseStatus.InProgress,
                                    pageNumber: 8,
                                    data: {
                                        [page7ReflectOnTheExerciseKey]:
                                            normalizeInput(
                                                page7ReflectOnTheExerciseInput,
                                            ),
                                    },
                                })
                            }
                        />
                    ),
                    8: (
                        <Page8
                            onContinue={() =>
                                onContinue({
                                    status: ExerciseStatus.Completed,
                                    goToNext: false,
                                })
                            }
                        />
                    ),
                }}
            />
        </Exercise>
    );
}

function Page1({ exercise, onContinue }: ExerciseViewProps) {
    const navigate = useNavigation();
    const body = [
        'Jeg føler mig ofte overvældet af alt det, jeg skal nå i min hverdag. Jeg bor til leje og er allerede flyttet 3 gange de sidste to år, og det presser mig virkelig ikke at have en fast base. Samtidig prøver jeg at være en god veninde, holde mig sund og finde tid til min familie. Det føles som om, jeg konstant løber fra det ene til det andet, og jeg har svært ved at finde tid til bare at trække vejret.',
        'Stressen begynder at påvirke mig – jeg har svært ved at koncentrere mig, og jeg føler mig træt hele tiden. Oven i det taler jeg ofte grimt til mig selv. Jeg siger ting som: "Hvorfor kan du ikke bare tage dig sammen?" eller "Alle andre klarer det jo, hvorfor kan du ikke?" Det gør det hele endnu sværere, for i stedet for at støtte mig selv, ender jeg med at slå mig selv i hovedet. Jeg vil så gerne klare det hele, men jeg er begyndt at spørge mig selv, om det overhovedet er muligt.',
    ];
    return (
        <>
            <img
                className="rounded-[10px]"
                src={'/images/falck/module-images/module3-pres-testimony.webp'}
            />
            <LabelBodySmall class="mt-10">
                Billedet er illustrativt
            </LabelBodySmall>
            <LabelHeader4 class="mt-30">{exercise.Name}</LabelHeader4>
            {body.map((x, idx) => (
                <LabelBody key={idx} className="mt-10">
                    {idx === 0 && (
                        <LabelDisplay4 class="inline-block">“</LabelDisplay4>
                    )}
                    {x}
                </LabelBody>
            ))}
            <IllustrationCritic className="mt-30" />
            <SecondaryButton
                onClick={() =>
                    onContinue({
                        status: ExerciseStatus.InProgress,
                        pageNumber: 2,
                        data: {},
                    })
                }
            >
                Gå i gang med øvelsen
            </SecondaryButton>
            <GhostButton
                className={'mt-20'}
                onClick={() => navigate.toModule()}
            >
                Vender tilbage senere
            </GhostButton>
        </>
    );
}

type Page2Props = {
    exercise: ExerciseDto;
    page2HasSeenVideo: string;
    onContinue: () => void;
};
function Page2({ exercise, page2HasSeenVideo, onContinue }: Page2Props) {
    return (
        <>
            <LabelDisplay2>Hvordan viser du omsorg?</LabelDisplay2>
            <LabelBody className="my-10">
                Det kan være svært at tale pænt til sig selv, når man er
                presset. Og ofte behandler vi os selv hårdere, end vi ville
                behandle dem vi holder af. Men tænk hvis du gav dig selv den
                samme omsorg og venlighed, som du ville give en ven. I denne
                øvelse guider vi dig til at blive klogere på, hvordan du vil
                være der for dig selv.
            </LabelBody>
            <LabelBody>Videoen guider dig igennem øvelsen.</LabelBody>
            <VideoExerciseView
                className="my-30"
                exercise={exercise}
                videoMetadata={{
                    PosterUrl:
                        'https://static.hejdoktor.dk/falcksundtsind/Falck+-+Sundt+Sind+-+Film+07+-+V05+-+Online+-+241220+-+JK.00_00_43_03.Still007.jpg',
                    VideoUrl:
                        'https://static.hejdoktor.dk/falcksundtsind/Falck+-+Sundt+Sind+-+Film+07+A+-+V05+-+Online+-+241220+-+JK.mp4',
                    MimeType: 'video/mp4',
                    Duration: '00:00:58',
                    Width: 1920,
                    Height: 1080,
                }}
                onVideoEnded={() => {
                    onContinue();
                }}
            />
            <Spacer />
            {page2HasSeenVideo === 'true' && (
                <SecondaryButton
                    onClick={() => {
                        onContinue();
                    }}
                >
                    Fortsæt øvelsen
                </SecondaryButton>
            )}
        </>
    );
}

type Page3Props = {
    page3HowWouldYouHelpAFriendInput: string;
    onChangePage3HowWouldYouHelpAFriendInput: (value: string) => void;
    onContinue: () => void;
};
function Page3({
    page3HowWouldYouHelpAFriendInput,
    onChangePage3HowWouldYouHelpAFriendInput,
    onContinue,
}: Page3Props) {
    return (
        <>
            <Card className="mb-30">
                <LabelDisplay1>Hvordan ville du hjælpe en ven?</LabelDisplay1>
                <LabelBody className="my-10">
                    Tænk over, hvordan du ville forsøge at være der for
                    personen.
                </LabelBody>
                <LabelBody>
                    Hvad ville du sige, og hvordan ville du hjælpe?
                </LabelBody>
                <Textarea
                    className="mb-10 mt-20"
                    text={page3HowWouldYouHelpAFriendInput}
                    onChangeText={onChangePage3HowWouldYouHelpAFriendInput}
                />
                <LabelBody>
                    Dit svar bliver gemt i logbogen, når du fortsætter.
                </LabelBody>
            </Card>
            <Spacer />
            <SecondaryButton onClick={onContinue}>
                Fortsæt øvelsen
            </SecondaryButton>
        </>
    );
}

type Page4Props = {
    exercise: ExerciseDto;
    page4HasSeenVideo: string;
    onContinue: () => void;
};
function Page4({ exercise, page4HasSeenVideo, onContinue }: Page4Props) {
    return (
        <div className="three-blobs-img-2 -mx-20 -mb-20 flex flex-1 flex-col">
            <Spacer />
            <LabelDisplay2 className="mx-auto">
                Fortsæt videoklippet...
            </LabelDisplay2>
            <div className="flex flex-col px-20">
                <VideoExerciseView
                    className="mb-30 mt-20"
                    exercise={exercise}
                    videoMetadata={{
                        PosterUrl:
                            'https://static.hejdoktor.dk/falcksundtsind/Falck+-+Sundt+Sind+-+Film+07+-+V05+-+Online+-+241220+-+JK.00_00_43_03.Still007.jpg',
                        VideoUrl:
                            'https://static.hejdoktor.dk/falcksundtsind/Falck+-+Sundt+Sind+-+Film+07+B+-+V05+-+Online+-+241220+-+JK.mp4',
                        MimeType: 'video/mp4',
                        Duration: '00:00:38',
                        Width: 1920,
                        Height: 1080,
                    }}
                    onVideoEnded={() => {
                        onContinue();
                    }}
                />
            </div>
            <Spacer />
            {page4HasSeenVideo === 'true' && (
                <SecondaryButton
                    className="mb-20"
                    onClick={() => {
                        onContinue();
                    }}
                >
                    Fortsæt øvelsen
                </SecondaryButton>
            )}
        </div>
    );
}

type Page5Props = {
    page5HowWouldYouHelpYourselfInput: string;
    onChangePage5HowWouldYouHelpYourselfInput: (value: string) => void;
    onContinue: () => void;
};
function Page5({
    page5HowWouldYouHelpYourselfInput,
    onChangePage5HowWouldYouHelpYourselfInput,
    onContinue,
}: Page5Props) {
    return (
        <>
            <Card className="mb-30">
                <LabelDisplay1 className="mb-10">
                    Hvordan ville du hjælpe dig selv?
                </LabelDisplay1>
                <LabelBody className="my-10">
                    Tænk nu over, hvordan du ville forsøge at være der for den
                    udgave af dig, der kan have det svært.
                </LabelBody>
                <LabelBody>
                    Hvad ville du sige, og hvordan ville du hjælpe?
                </LabelBody>
                <Textarea
                    className="mb-10 mt-20"
                    text={page5HowWouldYouHelpYourselfInput}
                    onChangeText={onChangePage5HowWouldYouHelpYourselfInput}
                />
                <LabelBody>
                    Dit svar bliver gemt i logbogen, når du fortsætter.
                </LabelBody>
            </Card>
            <Spacer />
            <SecondaryButton onClick={onContinue}>
                Fortsæt øvelsen
            </SecondaryButton>
        </>
    );
}

type Page6Props = {
    exercise: ExerciseDto;
    page6HasSeenVideo: string;
    onContinue: () => void;
};
function Page6({ exercise, page6HasSeenVideo, onContinue }: Page6Props) {
    return (
        <div className="three-blobs-img-3 -mx-20 flex flex-1 flex-col">
            <Spacer />
            <LabelDisplay2 className="mx-auto">
                Fortsæt videoklippet...
            </LabelDisplay2>
            <div className="flex flex-col px-20">
                <VideoExerciseView
                    className="mb-30 mt-20"
                    exercise={exercise}
                    videoMetadata={{
                        PosterUrl:
                            'https://static.hejdoktor.dk/falcksundtsind/Falck+-+Sundt+Sind+-+Film+07+-+V05+-+Online+-+241220+-+JK.00_00_43_03.Still007.jpg',
                        VideoUrl:
                            'https://static.hejdoktor.dk/falcksundtsind/Falck+-+Sundt+Sind+-+Film+07+C+-+V05+-+Online+-+241220+-+JK.mp4',
                        MimeType: 'video/mp4',
                        Duration: '00:00:36',
                        Width: 1920,
                        Height: 1080,
                    }}
                    onVideoEnded={() => {
                        onContinue();
                    }}
                />
            </div>
            <Spacer />
            {page6HasSeenVideo === 'true' && (
                <SecondaryButton
                    onClick={() => {
                        onContinue();
                    }}
                >
                    Fortsæt øvelsen
                </SecondaryButton>
            )}
        </div>
    );
}

type Page7Props = {
    page7ReflectOnTheExerciseInput: string;
    onChangePage7ReflectOnTheExerciseInput: (value: string) => void;
    onContinue: () => void;
};
function Page7({
    page7ReflectOnTheExerciseInput,
    onChangePage7ReflectOnTheExerciseInput,
    onContinue,
}: Page7Props) {
    return (
        <>
            <Card className="mb-30">
                <LabelDisplay1>Reflekter over øvelsen</LabelDisplay1>
                <LabelBody className="my-10">
                    Overvej nu, om der er forskel på den måde, du vil hjælpe en
                    ven på sammenlignet med dig selv.
                </LabelBody>
                <LabelBody>
                    Hvad kan du tage med dig fra øvelsen, så du bedre husker en
                    større forståelse og omsorg for dig selv, når du er presset?
                </LabelBody>
                <Textarea
                    className="mb-10 mt-20"
                    text={page7ReflectOnTheExerciseInput}
                    onChangeText={onChangePage7ReflectOnTheExerciseInput}
                />
                <LabelBody>
                    Dit svar bliver gemt i logbogen, når du fortsætter.
                </LabelBody>
            </Card>
            <Spacer />
            <SecondaryButton onClick={onContinue}>Fortsæt</SecondaryButton>
        </>
    );
}

type Page8Props = {
    onContinue: () => void;
};
function Page8({ onContinue }: Page8Props) {
    return (
        <>
            <LabelDisplay2 className="mb-10">Afrunding</LabelDisplay2>
            <LabelBody>
                Husk at du er ikke alene, alle kan have det svært. Du er bare et
                menneske – hverken mindre eller mere. Næste gang du f.eks.
                oplever at være opslugt af selvkritik, følelser af
                utilstrækkelighed eller tanker om at du skal kunne klare mere,
                så prøv de ting af, du har skrevet ned. Det kan have en
                beroligende effekt.
            </LabelBody>
            <Module7Exercise3Illustration1 className="mx-auto my-30" />
            <Spacer />
            <SecondaryButton onClick={onContinue}>
                Afslut øvelsen
            </SecondaryButton>
        </>
    );
}
